import { useCallback, useEffect, useState } from "react";

import { CategoryService } from "../services/category.service";

import {
  Category,
  CategoryFilters,
  CategoryPayload,
  Pagination,
} from "../types";

export const useCategories = () => {
  /* ------------------ States ------------------ */

  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);

  const [pagination, setPagination] = useState<Pagination | null>(null);

  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<CategoryFilters>({
    page: 1,
    limit: 10,
    search: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  /* ------------------ Fetch ------------------ */

  const fetchCategories = useCallback(
    async (customFilters?: Partial<CategoryFilters>) => {
      try {
        setLoading(true);
        setError(null);

        const query = {
          ...filters,
          ...customFilters,
        };

        const res = await CategoryService.getCategories(query);

        setCategories(res.data);

        setPagination(res.pagination);

        if (customFilters) {
          setFilters(query);
        }
      } catch (error: any) {
        console.error("Failed to fetch categories", error);
        setError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch categories"
        );
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  /* ------------------ Create ------------------ */

  const createCategory = async (payload: CategoryPayload) => {
    await CategoryService.createCategory(payload);

    await fetchCategories();
  };

  /* ------------------ Update ------------------ */

  const updateCategory = async (
    id: string,
    payload: CategoryPayload
  ) => {
    await CategoryService.updateCategory(id, payload);

    await fetchCategories();
  };

  /* ------------------ Delete ------------------ */

  const deleteCategory = async (id: string) => {
    await CategoryService.deleteCategory(id);

    await fetchCategories();
  };

  /* ------------------ Status ------------------ */

  const updateCategoryStatus = async (
    id: string,
    status: "ACTIVE" | "INACTIVE"
  ) => {
    await CategoryService.updateStatus(id, status);

    await fetchCategories();
  };

  /* ------------------ Search ------------------ */

  const searchCategories = (search: string) => {
    fetchCategories({
      search,
      page: 1,
    });
  };

  /* ------------------ Pagination ------------------ */

  const changePage = (
    page: number,
    limit: number
  ) => {
    fetchCategories({
      page,
      limit,
    });
  };

  /* ------------------ Sorting ------------------ */

  const sortCategories = (
    sortBy: string,
    sortOrder: "asc" | "desc"
  ) => {
    fetchCategories({
      sortBy,
      sortOrder,
    });
  };

  /* ------------------ Initial Load ------------------ */

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    loading,

    categories,

    pagination,

    filters,

    error,

    fetchCategories,

    createCategory,

    updateCategory,

    deleteCategory,

    updateCategoryStatus,

    searchCategories,

    changePage,

    sortCategories,
  };
};
